import { useState, useMemo, useCallback, useEffect, useRef, type CSSProperties } from 'react';
import { DeleteInvoiceModal } from './DeleteInvoiceModal';
import { deleteInvoiceCascade } from '../../../services/delete-invoice-service';
import { Button } from '@helios/ui/button';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qInvoicesByPatient,
  qInvoicesByEncounter,
  qInvoiceItems,
  qActiveAppointmentServices,
  type InvoiceRow,
  type InvoiceItemRow,
  type AppointmentServiceRow,
} from '@helios/data-access';
import {
  type InvoiceStatus,
  invoiceSummary,
  rowTotal,
  formatCurrency,
  formatDateShort,
} from './consultation-data';

interface InvoicesTabProps {
  patientId: string;
  patientName?: string;
  encounterId?: string;
  visitId?: string;
  initialInvoiceId?: string;
}

export function InvoicesTab({
  patientId,
  patientName = '',
  encounterId,
  visitId,
  initialInvoiceId,
}: InvoicesTabProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#78716c';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  // ─── Queries ───
  const invoiceQuery = encounterId
    ? qInvoicesByEncounter(patientId, encounterId)
    : qInvoicesByPatient(patientId);
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(invoiceQuery);

  const [selectedId, setSelectedId] = useState<string>(initialInvoiceId ?? '');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | InvoiceStatus>('all');
  const [savedToast, setSavedToast] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Auto-select: prefer initialInvoiceId, then first invoice when list changes
  useEffect(() => {
    if (invoiceRows.length === 0) return;
    if (initialInvoiceId && invoiceRows.find((r) => r.id === initialInvoiceId)) {
      setSelectedId(initialInvoiceId);
      return;
    }
    if (!invoiceRows.find((r) => r.id === selectedId)) {
      setSelectedId(invoiceRows[0].id);
    }
  }, [invoiceRows, selectedId, initialInvoiceId]);

  const filtered = useMemo(() => {
    let result = invoiceRows;
    if (statusFilter !== 'all') {
      result = result.filter((inv) => inv.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (inv) =>
          (inv.invoice_no ?? '').toLowerCase().includes(q) ||
          (inv.date_iso ?? '').includes(q) ||
          (inv.status ?? '').includes(q),
      );
    }
    return result;
  }, [invoiceRows, search, statusFilter]);

  const selectedInvoice = invoiceRows.find((inv) => inv.id === selectedId) ?? null;

  // ─── Writes ───

  const handleNewInvoice = useCallback(async () => {
    if (!db) return;
    const now = new Date().toISOString();
    const maxNum = invoiceRows.reduce((max, inv) => {
      const n = parseInt((inv.invoice_no ?? '').replace('INV-', ''), 10);
      return n > max ? n : max;
    }, 0);

    const invoiceId = crypto.randomUUID();
    const itemId = crypto.randomUUID();
    const invoiceNo = `INV-${String(maxNum + 1).padStart(5, '0')}`;

    // Create invoice
    await db.execute(
      `INSERT INTO invoices
        (id, org_id, clinic_id, patient_id, encounter_id, visit_id,
         invoice_no, patient_name, date_iso, status, notes,
         subtotal, discount_total, tax_total, grand_total, created_at, updated_at)
       VALUES (?, ?, NULL, ?, ?, ?,
         ?, ?, ?, ?, '',
         1500, 0, 0, 1500, ?, ?)`,
      [
        invoiceId,
        orgId,
        patientId,
        encounterId ?? null,
        visitId ?? null,
        invoiceNo,
        patientName,
        now.slice(0, 10),
        'draft',
        now,
        now,
      ],
    );

    // Create default item
    await db.execute(
      `INSERT INTO invoice_items
        (id, org_id, invoice_id, service_name, qty, unit_price, discount, tax_rate, sort_order, created_at, updated_at)
       VALUES (?, ?, ?, 'Consultation', 1, 1500, 0, 0, 0, ?, ?)`,
      [itemId, orgId, invoiceId, now, now],
    );

    setSelectedId(invoiceId);
  }, [db, orgId, patientId, patientName, invoiceRows, encounterId, visitId]);

  const handleMarkPaid = useCallback(async () => {
    if (!db || !selectedInvoice) return;
    const now = new Date().toISOString();
    const invoiceId = selectedInvoice.id;

    // 1) Mark invoice as paid
    await db.execute(`UPDATE invoices SET status = 'paid', updated_at = ? WHERE id = ?`, [
      now,
      invoiceId,
    ]);

    // 2) Create payment record (idempotent — check if one already exists)
    const existingPayments = await db.getAll<{ id: string }>(
      `SELECT id FROM invoice_payments WHERE invoice_id = ?`,
      [invoiceId],
    );
    if (existingPayments.length === 0) {
      const grandTotal = selectedInvoice.grand_total ?? 0;
      await db.execute(
        `INSERT INTO invoice_payments (id, org_id, invoice_id, amount_cents, method, paid_at, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          invoiceId,
          Math.round(grandTotal * 100),
          'cash',
          now,
          now,
          now,
        ],
      );
    }

    // 3) Update treatment records linked to this invoice — set amount_paid = amount_charged
    const linkedRecords = await db.getAll<{ id: string; amount_charged_cents: number }>(
      `SELECT id, amount_charged_cents FROM patient_treatment_records WHERE invoice_id = ?`,
      [invoiceId],
    );
    for (const rec of linkedRecords) {
      await db.execute(
        `UPDATE patient_treatment_records SET amount_paid_cents = ?, updated_at = ? WHERE id = ?`,
        [rec.amount_charged_cents, now, rec.id],
      );
    }
  }, [db, selectedInvoice, orgId]);

  const handleSave = useCallback(async () => {
    if (!db || !selectedInvoice) {
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2000);
      return;
    }
    const now = new Date().toISOString();
    const invoiceId = selectedInvoice.id;
    const invoiceDate = selectedInvoice.date_iso ?? now.split('T')[0];

    // Fetch current items
    const items = await db.getAll<InvoiceItemRow>(
      `SELECT * FROM invoice_items WHERE invoice_id = ?`,
      [invoiceId],
    );

    for (const item of items) {
      const addToTx = item.add_to_treatment === 1;
      const itemTotal = (item.qty ?? 1) * (item.unit_price ?? 0) - (item.discount ?? 0);
      const chargedCents = Math.round(Math.max(0, itemTotal) * 100);

      if (addToTx) {
        // Upsert treatment record for this invoice item
        const existing = await db.getAll<{ id: string }>(
          `SELECT id FROM patient_treatment_records WHERE invoice_item_id = ?`,
          [item.id],
        );
        if (existing.length > 0) {
          // Update
          await db.execute(
            `UPDATE patient_treatment_records SET date = ?, tooth_nos = ?, procedure_name = ?, amount_charged_cents = ?, notes = ?, updated_at = ? WHERE id = ?`,
            [
              invoiceDate,
              item.remarks || null,
              item.service_name ?? '',
              chargedCents,
              item.remarks || null,
              now,
              existing[0].id,
            ],
          );
        } else {
          // Insert
          await db.execute(
            `INSERT INTO patient_treatment_records (id, org_id, clinic_id, patient_id, encounter_id, invoice_id, invoice_item_id, date, tooth_nos, procedure_name, amount_charged_cents, amount_paid_cents, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              crypto.randomUUID(),
              orgId,
              null,
              patientId,
              selectedInvoice.encounter_id ?? null,
              invoiceId,
              item.id,
              invoiceDate,
              item.remarks || null,
              item.service_name ?? '',
              chargedCents,
              0,
              item.remarks || null,
              now,
              now,
            ],
          );
        }
      } else {
        // Remove treatment record if it was created from this invoice item
        await db.execute(`DELETE FROM patient_treatment_records WHERE invoice_item_id = ?`, [
          item.id,
        ]);
      }
    }

    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2000);
  }, [db, selectedInvoice, orgId, patientId]);

  const handleUpdateNotes = useCallback(
    async (notes: string) => {
      if (!db || !selectedInvoice) return;
      const now = new Date().toISOString();
      await db.execute(`UPDATE invoices SET notes = ?, updated_at = ? WHERE id = ?`, [
        notes,
        now,
        selectedInvoice.id,
      ]);
    },
    [db, selectedInvoice],
  );

  const handleUpdateReferenceNo = useCallback(
    async (refNo: string) => {
      if (!db || !selectedInvoice) return;
      const now = new Date().toISOString();
      await db.execute(`UPDATE invoices SET reference_no = ?, updated_at = ? WHERE id = ?`, [
        refNo || null,
        now,
        selectedInvoice.id,
      ]);
    },
    [db, selectedInvoice],
  );

  const handleUpdateReferenceType = useCallback(
    async (type: string) => {
      if (!db || !selectedInvoice) return;
      const now = new Date().toISOString();
      await db.execute(`UPDATE invoices SET reference_type = ?, updated_at = ? WHERE id = ?`, [
        type,
        now,
        selectedInvoice.id,
      ]);
    },
    [db, selectedInvoice],
  );

  const handleDeleteInvoice = useCallback(
    async (opts: { deletePayments: boolean; deleteTreatmentRecords: boolean }) => {
      if (!db || !selectedInvoice) return;
      setIsDeleting(true);
      try {
        await deleteInvoiceCascade({
          db,
          invoiceId: selectedInvoice.id,
          deletePayments: opts.deletePayments,
          deleteTreatmentRecords: opts.deleteTreatmentRecords,
        });
        setDeleteModalOpen(false);
        setSelectedId('');
      } catch (err) {
        console.error('[DeleteInvoice] Failed:', err);
      } finally {
        setIsDeleting(false);
      }
    },
    [db, selectedInvoice],
  );

  // Check if selected invoice has payments (for modal checkboxes)
  const selectedHasPayments = selectedInvoice?.status === 'paid';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', flex: 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Invoices
          </h2>
          <p
            style={{
              margin: '2px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Manage invoices related to consultations
          </p>
        </div>
        <Button variant="gradient" size="md" onClick={handleNewInvoice}>
          + New Invoice
        </Button>
      </div>

      {/* Split view */}
      <div style={{ display: 'flex', gap: 'var(--sp-4)', flex: 1, minHeight: 0 }}>
        {/* Left: Invoice List */}
        <div
          style={{
            width: 280,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
                display: 'flex',
                pointerEvents: 'none',
              }}
            >
              <NavIcon name="search" />
            </div>
            <input
              type="text"
              placeholder="Search invoice #, date, status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                height: 'var(--input-height)',
                padding: '0 var(--input-padding-x) 0 40px',
                borderRadius: 'var(--input-radius)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--input-text)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | InvoiceStatus)}
            style={selectStyle}
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="paid">Paid</option>
          </select>

          {/* List */}
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, overflowY: 'auto' }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: 'var(--sp-6)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                No invoices found
              </div>
            ) : (
              filtered.map((inv) => {
                const isSelected = inv.id === selectedId;
                return (
                  <button
                    key={inv.id}
                    type="button"
                    onClick={() => setSelectedId(inv.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 'var(--sp-3) var(--sp-4)',
                      border: 'none',
                      borderLeft: isSelected ? `3px solid ${primary}` : '3px solid transparent',
                      background: isSelected ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-semi)',
                          color: 'var(--text)',
                        }}
                      >
                        {inv.invoice_no}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          marginTop: 2,
                        }}
                      >
                        {inv.date_iso ? formatDateShort(inv.date_iso) : ''}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 4,
                      }}
                    >
                      <StatusPill status={(inv.status as InvoiceStatus) ?? 'draft'} />
                      <span
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text)',
                        }}
                      >
                        {formatCurrency(inv.grand_total ?? 0)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right: Invoice Detail */}
        <div
          style={{
            flex: 1,
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {selectedInvoice ? (
            <InvoiceDetail
              invoice={selectedInvoice}
              orgId={orgId}
              primary={primary}
              onMarkPaid={handleMarkPaid}
              onSave={handleSave}
              onUpdateNotes={handleUpdateNotes}
              onUpdateReferenceNo={handleUpdateReferenceNo}
              onUpdateReferenceType={handleUpdateReferenceType}
              onDelete={() => setDeleteModalOpen(true)}
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Select an invoice to view details
            </div>
          )}
        </div>
      </div>

      {/* Saved toast */}
      {savedToast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: 'var(--sp-3) var(--sp-5)',
            background: '#065f46',
            color: '#fff',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 100,
          }}
        >
          Saved
        </div>
      )}

      <DeleteInvoiceModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteInvoice}
        invoiceNo={selectedInvoice?.invoice_no ?? ''}
        isPaid={selectedInvoice?.status === 'paid'}
        hasPayments={selectedHasPayments}
        hasTreatmentRecords={false}
        isDeleting={isDeleting}
      />
    </div>
  );
}

// ─── Service Autocomplete ───

function ServiceAutocomplete({
  value,
  onChange,
  onSelectService,
  services,
}: {
  value: string;
  onChange: (name: string) => void;
  onSelectService: (name: string, priceCents: number | null, serviceId?: string) => void;
  services: AppointmentServiceRow[];
}) {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const blurTimeout = useRef<ReturnType<typeof setTimeout>>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!value || value.length < 1) return services.slice(0, 8);
    const q = value.toLowerCase();
    return services.filter((s) => s.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [value, services]);

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightIdx(-1);
  }, [filtered]);

  const handleFocus = () => setOpen(true);

  const handleBlur = () => {
    blurTimeout.current = setTimeout(() => setOpen(false), 150);
  };

  const handleSelect = (svc: AppointmentServiceRow) => {
    if (blurTimeout.current) clearTimeout(blurTimeout.current);
    onSelectService(svc.name ?? '', svc.price_cents, svc.id);
    setOpen(false);
    setHighlightIdx(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightIdx(-1);
      return;
    }
    if (!open || filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((prev) => {
        const next = prev < filtered.length - 1 ? prev + 1 : 0;
        // Scroll into view
        listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((prev) => {
        const next = prev > 0 ? prev - 1 : filtered.length - 1;
        listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    } else if (e.key === 'Enter' && highlightIdx >= 0 && highlightIdx < filtered.length) {
      e.preventDefault();
      handleSelect(filtered[highlightIdx]);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlightIdx(-1);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={cellInputStyle}
        placeholder="Type or search..."
      />
      {open && filtered.length > 0 && (
        <div
          ref={listRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 2,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 50,
            maxHeight: 220,
            overflowY: 'auto',
            minWidth: 200,
          }}
        >
          {filtered.map((svc, idx) => (
            <div
              key={svc.id}
              onMouseDown={() => handleSelect(svc)}
              onMouseEnter={() => setHighlightIdx(idx)}
              style={{
                padding: '6px 10px',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                background: idx === highlightIdx ? 'var(--bg-hover)' : 'transparent',
              }}
            >
              <span style={{ color: 'var(--text)' }}>{svc.name}</span>
              {svc.price_cents != null && svc.price_cents > 0 && (
                <span
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-xs)',
                    flexShrink: 0,
                  }}
                >
                  ₱{(svc.price_cents / 100).toFixed(2)}
                </span>
              )}
            </div>
          ))}
          <div
            style={{
              padding: '4px 10px',
              fontSize: 10,
              color: 'var(--text-placeholder)',
              borderTop: '1px solid var(--border)',
            }}
          >
            Or type any custom service name
          </div>
        </div>
      )}
      {open && value.length >= 2 && filtered.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 2,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 50,
            padding: '8px 10px',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            minWidth: 200,
          }}
        >
          No matching services — you can type any name
        </div>
      )}
    </div>
  );
}

// ─── Invoice Detail Panel ───

function InvoiceDetail({
  invoice,
  orgId,
  primary,
  onMarkPaid,
  onSave,
  onUpdateNotes,
  onUpdateReferenceNo,
  onUpdateReferenceType,
  onDelete,
}: {
  invoice: InvoiceRow;
  orgId: string;
  primary: string;
  onMarkPaid: () => void;
  onSave: () => void;
  onUpdateNotes: (notes: string) => void;
  onUpdateReferenceNo: (refNo: string) => void;
  onUpdateReferenceType: (type: string) => void;
  onDelete: () => void;
}) {
  const { db } = usePowerSyncDb();
  const { data: itemRows = [] } = usePowerSyncQuery<InvoiceItemRow>(qInvoiceItems(invoice.id));
  const { data: activeServices = [] } = usePowerSyncQuery<AppointmentServiceRow>(
    qActiveAppointmentServices(),
  );

  // ── Local item state (only written to DB on Save) ──

  type UiItem = {
    id: string;
    serviceName: string;
    serviceId: string | null;
    qty: number;
    unitPrice: number;
    discount: number;
    taxRate: number;
    remarks: string;
    addToTreatment: boolean;
    _isNew?: boolean; // local-only: needs INSERT on save
  };

  const toUiItems = (rows: InvoiceItemRow[]): UiItem[] =>
    rows.map((r) => ({
      id: r.id,
      serviceName: r.service_name ?? '',
      serviceId: r.service_id ?? null,
      qty: r.qty ?? 1,
      unitPrice: r.unit_price ?? 0,
      discount: r.discount ?? 0,
      taxRate: r.tax_rate ?? 0,
      remarks: r.remarks ?? '',
      addToTreatment: r.add_to_treatment === 1,
    }));

  const [localItems, setLocalItems] = useState<UiItem[]>(() => toUiItems(itemRows));

  // Sync from DB when itemRows change externally (e.g. after save round-trips)
  const lastSyncRef = useRef<string>('');
  useEffect(() => {
    const key = itemRows.map((r) => `${r.id}:${r.updated_at}`).join(',');
    if (key !== lastSyncRef.current) {
      lastSyncRef.current = key;
      setLocalItems(toUiItems(itemRows));
    }
  }, [itemRows]);

  const uiItems = localItems;
  const summary = invoiceSummary(uiItems);

  const handleUpdateItem = useCallback(
    (itemId: string, patch: Partial<Omit<UiItem, 'id' | '_isNew'>>) => {
      setLocalItems((prev) =>
        prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
      );
    },
    [],
  );

  const handleAddItem = useCallback(() => {
    const id = crypto.randomUUID();
    setLocalItems((prev) => [
      ...prev,
      {
        id,
        serviceName: '',
        serviceId: null,
        qty: 1,
        unitPrice: 0,
        discount: 0,
        taxRate: 0,
        remarks: '',
        addToTreatment: false,
        _isNew: true,
      },
    ]);
  }, []);

  // ── Flush all local items to DB (called by Save button) ──
  const flushItemsToDb = useCallback(async () => {
    if (!db) return;
    const now = new Date().toISOString();

    for (const item of localItems) {
      if (item._isNew) {
        await db.execute(
          `INSERT INTO invoice_items
            (id, org_id, invoice_id, service_name, service_id, qty, unit_price, discount, tax_rate, remarks, add_to_treatment, sort_order, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            item.id,
            orgId,
            invoice.id,
            item.serviceName,
            item.serviceId,
            item.qty,
            item.unitPrice,
            item.discount,
            item.taxRate,
            item.remarks || null,
            item.addToTreatment ? 1 : 0,
            localItems.indexOf(item),
            now,
            now,
          ],
        );
      } else {
        await db.execute(
          `UPDATE invoice_items SET service_name = ?, service_id = ?, qty = ?, unit_price = ?, discount = ?, tax_rate = ?, remarks = ?, add_to_treatment = ?, updated_at = ? WHERE id = ?`,
          [
            item.serviceName,
            item.serviceId,
            item.qty,
            item.unitPrice,
            item.discount,
            item.taxRate,
            item.remarks || null,
            item.addToTreatment ? 1 : 0,
            now,
            item.id,
          ],
        );
      }
    }

    // Update invoice totals
    await db.execute(
      `UPDATE invoices SET subtotal = ?, discount_total = ?, tax_total = ?, grand_total = ?, updated_at = ? WHERE id = ?`,
      [
        summary.subtotal,
        summary.discountTotal,
        summary.taxTotal,
        summary.grandTotal,
        now,
        invoice.id,
      ],
    );

    // Clear _isNew flags
    setLocalItems((prev) => prev.map((item) => ({ ...item, _isNew: undefined })));
  }, [db, orgId, invoice.id, localItems, summary]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' }}>
      {/* Header */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-5)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Invoice {invoice.invoice_no}
          </span>
          <StatusPill status={(invoice.status as InvoiceStatus) ?? 'draft'} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {invoice.status !== 'paid' && (
            <button
              type="button"
              onClick={onMarkPaid}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                height: 34,
                padding: '0 var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid #16a34a',
                background: 'none',
                color: '#16a34a',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Mark as Paid
            </button>
          )}
          <Button
            variant="gradient"
            size="md"
            onClick={async () => {
              await flushItemsToDb();
              onSave();
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <NavIcon name="save" />
              Save
            </span>
          </Button>
          <button
            type="button"
            style={{
              width: 34,
              height: 34,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <NavIcon name="dots-vertical" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            title="Delete Invoice"
            style={{
              width: 34,
              height: 34,
              borderRadius: 'var(--radius-md)',
              border: '1px solid #fecaca',
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <NavIcon name="trash" />
          </button>
        </div>
      </div>

      {/* Meta */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-5)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          display: 'flex',
          gap: 'var(--sp-4)',
        }}
      >
        <span>
          Patient: <strong style={{ color: 'var(--text)' }}>{invoice.patient_name}</strong>
        </span>
        <span>
          Date:{' '}
          <strong style={{ color: 'var(--text)' }}>
            {invoice.date_iso ? formatDateShort(invoice.date_iso) : ''}
          </strong>
        </span>
      </div>

      {/* Items */}
      <div style={{ padding: 'var(--sp-4) var(--sp-5)' }}>
        <h3
          style={{
            margin: '0 0 var(--sp-3)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          Items
        </h3>
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'visible',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['SERVICE', 'REMARKS', 'QTY', 'UNIT PRICE', 'DISC.', 'TAX %', 'TOTAL', ''].map(
                  (col) => (
                    <th key={col} style={thStyle}>
                      {col}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {uiItems.map((item) => (
                <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={tdStyle}>
                    <ServiceAutocomplete
                      value={item.serviceName}
                      onChange={(name) => handleUpdateItem(item.id, { serviceName: name })}
                      onSelectService={(name, priceCents, svcId) => {
                        const patch: Parameters<typeof handleUpdateItem>[1] = {
                          serviceName: name,
                          serviceId: svcId ?? null,
                        };
                        if (item.unitPrice === 0 && priceCents != null && priceCents > 0) {
                          patch.unitPrice = priceCents / 100;
                        }
                        handleUpdateItem(item.id, patch);
                      }}
                      services={activeServices}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={item.remarks}
                      onChange={(e) => handleUpdateItem(item.id, { remarks: e.target.value })}
                      placeholder="e.g. Tooth #14"
                      style={{ ...cellInputStyle, width: 110 }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      value={item.qty}
                      min={1}
                      onChange={(e) =>
                        handleUpdateItem(item.id, {
                          qty: Math.max(1, parseInt(e.target.value) || 1),
                        })
                      }
                      style={{ ...cellInputStyle, width: 50, textAlign: 'center' }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                        ₱
                      </span>
                      <input
                        type="number"
                        value={item.unitPrice}
                        min={0}
                        onChange={(e) =>
                          handleUpdateItem(item.id, {
                            unitPrice: Math.max(0, parseFloat(e.target.value) || 0),
                          })
                        }
                        style={{ ...cellInputStyle, width: 70 }}
                      />
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                        ₱
                      </span>
                      <input
                        type="number"
                        value={item.discount}
                        min={0}
                        onChange={(e) =>
                          handleUpdateItem(item.id, {
                            discount: Math.max(0, parseFloat(e.target.value) || 0),
                          })
                        }
                        style={{ ...cellInputStyle, width: 60 }}
                      />
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      value={item.taxRate}
                      min={0}
                      max={100}
                      onChange={(e) =>
                        handleUpdateItem(item.id, {
                          taxRate: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)),
                        })
                      }
                      style={{ ...cellInputStyle, width: 50, textAlign: 'center' }}
                    />
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: 'var(--font-weight-medium)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatCurrency(rowTotal(item))}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <label
                      title="Add to Treatment Record"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-xs)',
                        color: item.addToTreatment
                          ? 'var(--color-primary)'
                          : 'var(--text-placeholder)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={item.addToTreatment}
                        onChange={(e) =>
                          handleUpdateItem(item.id, { addToTreatment: e.target.checked })
                        }
                        style={{ accentColor: 'var(--color-primary)' }}
                      />
                      Tx
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={handleAddItem}
          style={{
            marginTop: 'var(--sp-2)',
            border: 'none',
            background: 'none',
            color: primary,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            padding: 'var(--sp-1) 0',
          }}
        >
          + Add item
        </button>
      </div>

      {/* Reference No */}
      <div style={{ padding: '0 var(--sp-5) var(--sp-4)' }}>
        <h3
          style={{
            margin: '0 0 var(--sp-2)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          Reference No.
        </h3>
        {/* Official / Unofficial toggle */}
        <div
          style={{
            display: 'flex',
            background: 'var(--bg-surface-2)',
            borderRadius: 'var(--radius-md)',
            padding: 2,
            gap: 2,
            marginBottom: 8,
            width: 'fit-content',
          }}
        >
          {(['official', 'unofficial'] as const).map((t) => {
            const active = (invoice.reference_type ?? 'unofficial') === t;
            return (
              <button
                key={t}
                onClick={() => onUpdateReferenceType(t)}
                style={{
                  height: 28,
                  padding: '0 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: active ? 'var(--bg-surface)' : 'transparent',
                  boxShadow: active ? 'var(--shadow-sm)' : 'none',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: active ? 'var(--text)' : 'var(--text-muted)',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
        <input
          type="text"
          value={invoice.reference_no ?? ''}
          onChange={(e) => onUpdateReferenceNo(e.target.value)}
          placeholder={
            (invoice.reference_type ?? 'unofficial') === 'official'
              ? 'e.g. OR-2026-001'
              : 'e.g. UR-001, receipt #'
          }
          style={{
            width: '100%',
            height: 36,
            padding: '0 var(--sp-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-input)',
            background: 'var(--input-bg)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--input-text)',
            outline: 'none',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Notes */}
      <div style={{ padding: '0 var(--sp-5) var(--sp-4)' }}>
        <h3
          style={{
            margin: '0 0 var(--sp-2)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          Notes
        </h3>
        <textarea
          value={invoice.notes ?? ''}
          onChange={(e) => onUpdateNotes(e.target.value)}
          placeholder="Add billing notes..."
          rows={3}
          style={{
            width: '100%',
            padding: 'var(--sp-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-input)',
            background: 'var(--input-bg)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--input-text)',
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Summary */}
      <div style={{ padding: '0 var(--sp-5) var(--sp-5)' }}>
        <h3
          style={{
            margin: '0 0 var(--sp-3)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          Summary
        </h3>
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          <SummaryRow label="Subtotal" value={formatCurrency(summary.subtotal)} />
          <SummaryRow label="Discount" value={`-${formatCurrency(summary.discountTotal)}`} />
          <SummaryRow label="Tax" value={formatCurrency(summary.taxTotal)} />
          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 'var(--sp-2)',
              marginTop: 'var(--sp-1)',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Total
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--text)',
              }}
            >
              {formatCurrency(summary.grandTotal)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Shared sub-components ───

function StatusPill({ status }: { status: InvoiceStatus }) {
  const styles: Record<InvoiceStatus, { bg: string; color: string }> = {
    draft: { bg: '#f1f5f9', color: '#64748b' },
    issued: { bg: '#fef3c7', color: '#92400e' },
    paid: { bg: '#d1fae5', color: '#065f46' },
  };
  const s = styles[status];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>{value}</span>
    </div>
  );
}

// ─── Styles ───

const thStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  textAlign: 'left',
  fontSize: '10px',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  fontSize: 'var(--font-size-sm)',
  verticalAlign: 'middle',
};

const cellInputStyle: CSSProperties = {
  height: 30,
  padding: '0 var(--sp-2)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-8) 0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'none',
  width: '100%',
  boxSizing: 'border-box',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
};
